import { Badge, Button, Combobox, Group, Modal, Stack, Text, TextInput, useCombobox } from '@mantine/core'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ControlDataSchema } from '@/api/main/types.gen'

interface Props {
  opened: boolean
  onClose: () => void
  source: ControlDataSchema | null
  schemas: ControlDataSchema[]
  isSubmitting: boolean
  onSubmit: (sourceSchema: string, destinationSchema: string) => void
}

const NEW_SCHEMA_PATTERN = /^[a-z0-9_]+$/

type Step = 'form' | 'confirm'

export function CopySchemaModal({ opened, onClose, source, schemas, isSubmitting, onSubmit }: Props) {
  const { t } = useTranslation('controlData')
  const combobox = useCombobox()
  const [search, setSearch] = useState('')
  const [step, setStep] = useState<Step>('form')
  const [pendingDestination, setPendingDestination] = useState('')
  const [pendingIsNew, setPendingIsNew] = useState(false)

  useEffect(() => {
    if (opened) {
      setSearch('')
      setStep('form')
      setPendingDestination('')
      setPendingIsNew(false)
    }
  }, [opened])

  const trimmedSearch = search.trim()

  // Existing schemas (excluding the source) sorted descending, filtered by the search term.
  const options = useMemo(() => {
    if (!source) return []
    return schemas
      .filter(s => s.schema_name !== source.schema_name)
      .filter(s => (trimmedSearch ? s.schema_name.toLowerCase().includes(trimmedSearch.toLowerCase()) : true))
      .sort((a, b) => b.schema_name.localeCompare(a.schema_name))
  }, [schemas, source, trimmedSearch])

  if (!source) return null

  const matchesExisting = schemas.some(s => s.schema_name === trimmedSearch)
  const showCreateOption =
    trimmedSearch.length > 0 &&
    !matchesExisting &&
    trimmedSearch !== source.schema_name &&
    NEW_SCHEMA_PATTERN.test(trimmedSearch)

  const requestConfirm = (destination: string, isNew: boolean) => {
    setPendingDestination(destination)
    setPendingIsNew(isNew)
    setStep('confirm')
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t('copySchema')} padding="lg" size="lg" yOffset="5vh">
      <Stack style={{ minHeight: 240 }}>
        {step === 'form' ? (
          <>
            <Text size="sm">
              {t('copyFromLabel')}{' '}
              <Text component="span" fw={600} ff="monospace">
                {source.schema_name}
              </Text>
            </Text>

            <Combobox
              store={combobox}
              onOptionSubmit={value => {
                if (value === '__create__') {
                  requestConfirm(trimmedSearch, true)
                } else {
                  requestConfirm(value, false)
                }
                combobox.closeDropdown()
              }}
            >
              <Combobox.Target>
                <TextInput
                  label={t('destinationSchema')}
                  description={t('destinationSchemaHelp')}
                  placeholder={t('destinationPlaceholder')}
                  value={search}
                  onChange={e => {
                    setSearch(e.currentTarget.value)
                    combobox.openDropdown()
                    combobox.updateSelectedOptionIndex()
                  }}
                  onClick={() => combobox.openDropdown()}
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => combobox.closeDropdown()}
                  data-autofocus
                />
              </Combobox.Target>
              <Combobox.Dropdown mah="40vh" style={{ overflowY: 'auto' }} pb="xs">
                <Combobox.Options>
                  {showCreateOption ? (
                    <Combobox.Option value="__create__">
                      <Group gap="xs">
                        <Badge color="blue" size="sm" variant="light">
                          {t('new')}
                        </Badge>
                        <Text size="sm">{t('createNewSchema', { name: trimmedSearch })}</Text>
                      </Group>
                    </Combobox.Option>
                  ) : null}
                  {options.map(schema => (
                    <Combobox.Option value={schema.schema_name} key={schema.schema_name}>
                      <Group justify="space-between" gap="xs">
                        <Text size="sm" ff="monospace">
                          {schema.schema_name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {schema.map_version ? `v${schema.map_version}` : null}
                        </Text>
                      </Group>
                    </Combobox.Option>
                  ))}
                  {!showCreateOption && options.length === 0 ? <Combobox.Empty>{t('noSchemas')}</Combobox.Empty> : null}
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>

            <Group justify="flex-end" gap="xs" mt="md">
              <Button variant="default" onClick={onClose}>
                {t('cancel')}
              </Button>
            </Group>
          </>
        ) : (
          <Stack>
            <Text fw={600}>{t('confirmCopyTitle')}</Text>
            <Text size="sm">{t('copySummary', { source: source.schema_name, destination: pendingDestination })}</Text>
            <Text size="sm" c={pendingIsNew ? 'dimmed' : 'red'} fw={pendingIsNew ? undefined : 700}>
              {pendingIsNew
                ? t('confirmCopyNewInfo', { source: source.schema_name, destination: pendingDestination })
                : t('confirmCopyOverwriteWarning', { destination: pendingDestination })}
            </Text>
            <Group justify="flex-end" gap="xs" mt="md">
              <Button variant="default" onClick={() => setStep('form')}>
                {t('back')}
              </Button>
              <Button
                color={pendingIsNew ? 'blue' : 'red'}
                loading={isSubmitting}
                onClick={() => onSubmit(source.schema_name, pendingDestination)}
              >
                {t('confirm')}
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}
